import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BillingService from '../../services/BillingService';
import ClinicSettingsService from '../../services/ClinicSettingsService';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarTodayIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';

const BillingList = () => {
  const navigate = useNavigate();
  
  const [billingRecords, setBillingRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const parseApiDate = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  
  useEffect(() => {
    const fetchBillingRecords = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // In a real application, you would fetch from your API
        // For now, we'll use mock data
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Fetch real data from API
        const response = await BillingService.getAllInvoices();
        const invoices = response.data || response;
        
        console.log('API Response:', response);
        console.log('Invoices array:', invoices);
        console.log('Number of invoices:', invoices ? invoices.length : 0);
        
        // Transform the data to match the expected format
        const billingRecords = invoices.map(invoice => {
          console.log('Processing invoice:', invoice);
          
          // Handle patient name - API returns patient object with firstName/lastName (camelCase)
          let patientName = 'Unknown Patient';
          if (invoice.patient && invoice.patient.firstName && invoice.patient.lastName) {
            patientName = `${invoice.patient.firstName} ${invoice.patient.lastName}`;
          }
          
          const parsedDate = parseApiDate(invoice.invoiceDate || invoice.InvoiceDate);
          
          const amount = parseFloat(invoice.totalAmount || invoice.TotalAmount || 0);
          
          return {
            id: invoice.invoiceId || invoice.InvoiceId,
            patientName: patientName,
            patientId: invoice.patientId || invoice.PatientId,
            date: parsedDate,
            invoiceNumber: invoice.invoiceNumber || invoice.InvoiceNumber || '',
            description: invoice.description || invoice.Description || invoice.notes || invoice.Notes || 'Medical Services',
            amount: isNaN(amount) ? 0 : amount,
            status: invoice.status || invoice.Status || 'Pending',
            paymentMethod: invoice.paymentMethod || invoice.PaymentMethod
          };
        });
        
        console.log('Transformed billing records:', billingRecords);
        setBillingRecords(billingRecords);
        setFilteredRecords(billingRecords);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching billing records:', error);
        setError('Failed to load billing records. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchBillingRecords();
  }, []);
  
  useEffect(() => {
    // Filter billing records based on search term, status, and date
    let filtered = billingRecords;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(record => 
        record.patientName.toLowerCase().includes(term) ||
        record.invoiceNumber.toLowerCase().includes(term) ||
        record.description.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(record => record.status === statusFilter);
    }
    
    if (dateFilter) {
      const filterDate = parseApiDate(dateFilter);
      filtered = filtered.filter(record => {
        const recordDate = parseApiDate(record.date);
        if (!filterDate || !recordDate) return false;
        return (
          recordDate.getDate() === filterDate.getDate() &&
          recordDate.getMonth() === filterDate.getMonth() &&
          recordDate.getFullYear() === filterDate.getFullYear()
        );
      });
    }
    
    setFilteredRecords(filtered);
    setPage(0); // Reset to first page when filtering
  }, [searchTerm, statusFilter, dateFilter, billingRecords]);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  const clearSearch = () => {
    setSearchTerm('');
  };
  
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };
  
  const handleDateFilterChange = (date) => {
    setDateFilter(date);
  };
  
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateFilter(null);
  };
  
  const handleViewBilling = (billingId) => {
    navigate(`/billing/${billingId}`);
  };
  
  const handleEditBilling = (billingId) => {
    console.log('Editing billing record with ID:', billingId);
    navigate(`/billing/${billingId}/edit`);
  };
  
  const handleDeleteBilling = async (billingId) => {
    if (window.confirm('Are you sure you want to delete this billing record?')) {
      try {
        await BillingService.deleteInvoice(billingId);
        
        // Remove from local state after successful deletion
        setBillingRecords(billingRecords.filter(record => record.id !== billingId));
        
        // Show success message
        alert('Invoice deleted successfully.');
      } catch (error) {
        console.error('Error deleting billing record:', error);
        alert('Failed to delete billing record. Please try again.');
      }
    }
  };

  const handlePrintBilling = async (billingId) => {
    try {
      const response = await BillingService.getInvoiceById(billingId);
      const invoice = response.data || response;
      const invoiceDate = parseApiDate(invoice.invoiceDate || invoice.InvoiceDate);
      const invoiceDiscount = parseFloat(invoice.discountAmount || invoice.DiscountAmount || 0) || 0;
      const showTaxColumn =
        (invoice.totalTaxAmount || invoice.TotalTaxAmount || invoice.taxAmount || invoice.TaxAmount || 0) > 0 ||
        (invoice.invoiceItems || invoice.InvoiceItems || []).some(item => ((item.taxAmount || item.TaxAmount || 0) > 0));
      const showDiscountColumn =
        invoiceDiscount > 0 || (invoice.invoiceItems || invoice.InvoiceItems || []).some(item => ((item.discount || item.Discount || 0) > 0));
      const columnCount = 5 + (showDiscountColumn ? 1 : 0) + (showTaxColumn ? 1 : 0);
      const summaryEmptyColSpan = Math.max(1, columnCount - 2);
      const billingData = {
        id: invoice.invoiceId || invoice.InvoiceId,
        patientName: invoice.patient 
          ? `${(invoice.patient.firstName || invoice.patient.FirstName || '')} ${(invoice.patient.lastName || invoice.patient.LastName || '')}`.trim() || 'Unknown Patient'
          : (invoice.Patient ? `${invoice.Patient.FirstName} ${invoice.Patient.LastName}` : 'Unknown Patient'),
        date: invoiceDate ? invoiceDate.toLocaleDateString() : '',
        invoiceNumber: invoice.invoiceNumber || invoice.InvoiceNumber || '',
        description: invoice.description || invoice.Description || 'Medical Services',
        paymentMethod: invoice.paymentMethod || invoice.PaymentMethod || '',
        items: (invoice.invoiceItems || invoice.InvoiceItems || []).map(item => ({
          description: item.notes || item.Notes || 
                       item.billingItem?.itemName || item.billingItem?.ItemName || 
                       item.BillingItem?.itemName || item.BillingItem?.ItemName || 
                       item.BillingItem?.Description || 'Unknown Item',
          quantity: item.quantity || item.Quantity || 1,
          unitPrice: item.unitPrice || item.UnitPrice || 0,
          discount: item.discount || item.Discount || 0,
          subtotal: item.subtotal || item.Subtotal || (((item.quantity || item.Quantity || 1) * (item.unitPrice || item.UnitPrice || 0)) - (item.discount || item.Discount || 0)),
          taxAmount: item.taxAmount || item.TaxAmount || 0,
          total: item.totalAmount || item.TotalAmount || ((((item.quantity || item.Quantity || 1) * (item.unitPrice || item.UnitPrice || 0)) - (item.discount || item.Discount || 0)) + (item.taxAmount || item.TaxAmount || 0))
        })),
        subtotal: invoice.subtotalAmount || invoice.SubtotalAmount || invoice.totalAmount || invoice.TotalAmount || 0,
        totalTaxAmount: invoice.totalTaxAmount || invoice.TotalTaxAmount || invoice.taxAmount || invoice.TaxAmount || 0,
        discount: invoiceDiscount,
        total: invoice.totalAmount || invoice.TotalAmount || 0,
        notes: invoice.notes || invoice.Notes || ''
      };

      let companyName = 'Clinic Management';
      let logoDataUrl = null;
      try {
        const settingsRes = await ClinicSettingsService.getSettings();
        const settings = settingsRes?.data ?? settingsRes;
        companyName = settings?.companyName ?? settings?.CompanyName ?? companyName;
        logoDataUrl = settings?.logoDataUrl ?? settings?.LogoDataUrl ?? null;
      } catch {
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        try { window.print(); } catch (e) {}
        return;
      }

      const printStyles = `
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #000; }
          .invoice-header { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .invoice-brand { display: flex; align-items: center; gap: 12px; }
          .company-logo { width: 56px; height: 56px; object-fit: contain; }
          .company-name { font-size: 16px; font-weight: bold; margin-bottom: 2px; }
          .invoice-title { font-size: 24px; font-weight: bold; }
          .invoice-details { margin-bottom: 20px; }
          .invoice-details div { margin-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
          .text-right { text-align: right; }
          .total-row { font-weight: bold; }
          .notes { margin-top: 30px; border-top: 1px solid #ddd; padding-top: 10px; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      `;

      const formatMYR = (amount) => new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(amount);
      const escapeHtml = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

      const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Invoice ${billingData.invoiceNumber}</title>
            ${printStyles}
          </head>
          <body>
            <div class="invoice-header">
              <div class="invoice-brand">
                ${logoDataUrl ? `<img class="company-logo" src="${logoDataUrl}" alt="Logo" />` : ''}
                <div>
                  <div class="company-name">${escapeHtml(companyName)}</div>
                  <div class="invoice-title">INVOICE</div>
                </div>
              </div>
              <div>
                <div><strong>Invoice #:</strong> ${billingData.invoiceNumber}</div>
                <div><strong>Date:</strong> ${billingData.date}</div>
                <div><strong>Status:</strong> ${invoice.status || invoice.Status || 'Pending'}</div>
              </div>
            </div>
            <div class="invoice-details">
              <div><strong>Patient:</strong> ${billingData.patientName}</div>
              <div><strong>Description:</strong> ${billingData.description}</div>
              ${billingData.paymentMethod ? `<div><strong>Payment Method:</strong> ${billingData.paymentMethod}</div>` : ''}
            </div>
            <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th class="text-right">Quantity</th>
                    <th class="text-right">Unit Price</th>
                    ${showDiscountColumn ? `<th class="text-right">Discount</th>` : ''}
                    <th class="text-right">Subtotal</th>
                    ${showTaxColumn ? `<th class="text-right">Tax</th>` : ''}
                    <th class="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${billingData.items.map(item => `
                    <tr>
                      <td>${item.description}</td>
                      <td class="text-right">${item.quantity}</td>
                      <td class="text-right">${formatMYR(item.unitPrice)}</td>
                      ${showDiscountColumn ? `<td class="text-right">${(item.discount || 0) > 0 ? formatMYR(item.discount || 0) : '-'}</td>` : ''}
                      <td class="text-right">${formatMYR(item.subtotal || 0)}</td>
                      ${showTaxColumn ? `<td class="text-right">${(item.taxAmount || 0) > 0 ? formatMYR(item.taxAmount || 0) : '-'}</td>` : ''}
                      <td class="text-right">${formatMYR(item.total)}</td>
                    </tr>
                  `).join('')}
                  ${showTaxColumn ? `
                    <tr>
                      <td colspan="${summaryEmptyColSpan}"></td>
                      <td class="text-right">Tax</td>
                      <td class="text-right">${formatMYR(billingData.totalTaxAmount)}</td>
                    </tr>
                  ` : ''}
                  ${billingData.discount > 0 ? `
                    <tr>
                      <td colspan="${summaryEmptyColSpan}"></td>
                      <td class="text-right">Discount</td>
                      <td class="text-right">-${formatMYR(billingData.discount)}</td>
                    </tr>
                  ` : ''}
                  <tr class="total-row">
                    <td colspan="${summaryEmptyColSpan}"></td>
                    <td class="text-right">Total</td>
                    <td class="text-right">${formatMYR(billingData.total)}</td>
                  </tr>
                </tbody>
              </table>
            ${billingData.notes ? `
              <div class="notes">
                <strong>Notes:</strong>
                <p>${billingData.notes}</p>
              </div>
            ` : ''}
          </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = function() { setTimeout(() => { printWindow.print(); }, 250); };
    } catch (error) {
      console.error('Error printing invoice:', error);
      alert('Failed to print invoice.');
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'Overdue':
        return 'error';
      default:
        return 'default';
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Billing & Invoices
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/billing/new')}
        >
          New Invoice
        </Button>
      </Box>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Search"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by patient, invoice number..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="status-filter-label">Payment Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  label="Payment Status"
                  onChange={handleStatusFilterChange}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="Paid">Paid</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Filter by Date"
                  value={dateFilter}
                  onChange={handleDateFilterChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined'
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                disabled={!searchTerm && statusFilter === 'all' && !dateFilter}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="billing records table">
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No billing records found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRecords
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((record) => (
                    <TableRow key={record.id} hover>
                      <TableCell>{record.invoiceNumber}</TableCell>
                      <TableCell>
                        <Button
                          sx={{ p: 0, textTransform: 'none', justifyContent: 'flex-start' }}
                          onClick={() => navigate(`/patients/${record.patientId}`)}
                        >
                          {record.patientName}
                        </Button>
                      </TableCell>
                      <TableCell>{record.date ? record.date.toLocaleDateString() : '-'}</TableCell>
                      <TableCell>{record.description}</TableCell>
                      <TableCell align="right">{formatCurrency(record.amount)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={record.status}
                          color={getStatusColor(record.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleViewBilling(record.id)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEditBilling(record.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handlePrintBilling(record.id)}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteBilling(record.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRecords.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default BillingList;
